import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, absent, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the departures board does to the two readings behind it, away from any rendering.
 *
 * **This screen is in no design file.** Every other page on this console is built pixel for pixel
 * from `19 WP18 Dispatch Operations.dc.html`; scheduled departures were asked for after that
 * bundle was cut, so there is nothing to copy. It is built in the same language instead — the same
 * page chrome, the same grid chrome, the same state badges, the same tokens — and where a choice
 * had no precedent it is stated here rather than left to be inferred from the markup.
 *
 * ## Why two readings and not one
 *
 * MOB-04 answers departures with a `routeId`, and a board that drew a UUID where a person expects
 * `Nairobi → Nakuru` would be a board nobody reads. So the routes are listed once and joined by
 * identifier. The set is bounded by how many routes a tenant runs, which is tens, and the listing
 * takes only a cursor — so this is the whole set rather than a window onto it.
 *
 * Occupancy is **not** a third reading. It arrives on each row of the listing, which is a change
 * made for this board: the alternative was one `getDeparture` per row on every refresh, and a
 * board is a thing that refreshes.
 */

/** A departure as MOB-04's listing answers it. */
export interface DepartureRow {
  readonly tenantId: string;
  readonly departureId: string;
  readonly routeId: string;
  readonly vehicleId: string;
  readonly productId: string;
  readonly departsAt: string;
  readonly status: "scheduled" | "boarding" | "departed" | "cancelled";
  readonly cancelledReason?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly occupancy: {
    readonly total: number;
    readonly free: number;
    readonly held: number;
    readonly sold: number;
  };
}

/** What the board needs from a route, which is its name. */
export interface RouteRow {
  readonly routeId: string;
  readonly name: string;
  readonly status: "active" | "retired";
}

/** A row as the board draws it: the departure, with its route named. */
export interface BoardRow extends DepartureRow {
  /** GEO-06's name for the route, or the identifier where no route names it. */
  readonly routeName: string;
  /** True where the name is the identifier, so the cell can say why. */
  readonly routeMissing: boolean;
}

/**
 * The four states MOB-04 publishes, in the design system's own classes.
 *
 * `boarding` is `active` rather than `caution`: a bus with its doors open is the system working,
 * not a thing to worry about. `cancelled` is `closed` and not `failed` — a cancelled departure is
 * a decision somebody made, and the red class on this console is reserved for what went wrong.
 */
const BADGE: Readonly<
  Record<DepartureRow["status"], { readonly classKey: StateClassKey; readonly label: string }>
> = {
  scheduled: { classKey: "waiting", label: "scheduled" },
  boarding: { classKey: "active", label: "boarding" },
  departed: { classKey: "done", label: "departed" },
  cancelled: { classKey: "closed", label: "cancelled" },
};

export function badgeFor(status: DepartureRow["status"]): {
  readonly classKey: StateClassKey;
  readonly label: string;
} {
  return BADGE[status];
}

/** Join the departures to their routes, keeping MOB-04's order, which is soonest first. */
export function boardRows(
  departures: readonly DepartureRow[],
  routes: readonly RouteRow[],
): readonly BoardRow[] {
  const byId = new Map(routes.map((route) => [route.routeId, route]));
  return departures.map((departure) => {
    const route = byId.get(departure.routeId);
    return {
      ...departure,
      /*
       * The identifier where no route names it, rather than a blank or a dash. A retired route
       * still names the departures already scheduled on it, and a route listing that was paged
       * short would otherwise silently blank a column — the identifier is at least something an
       * operator can search for.
       */
      routeName: route?.name ?? departure.routeId,
      routeMissing: route === undefined,
    };
  });
}

/**
 * How full a departure is, as the design system's own `11 / 14`.
 *
 * Sold against total, not sold-plus-held: a held seat is not a sale, and a board that counted it
 * as one would tell an operator a bus is full while seats are still being paid for — and then
 * show it emptier a quarter of an hour later when the holds ran out. The holds are in the title
 * and in the row's panel, where somebody deciding whether to add a vehicle can see them.
 */
export function occupancyCell(row: BoardRow): {
  readonly text: string;
  readonly title: string;
} {
  const { total, free, held, sold } = row.occupancy;
  const parts = [`${String(sold)} sold of ${String(total)}.`];
  if (held > 0) {
    parts.push(
      `${String(held)} held while somebody pays, which is not a sale yet and is not counted as one.`,
    );
  }
  parts.push(`${String(free)} still free.`);
  return { text: `${String(sold)} / ${String(total)}`, title: parts.join(" ") };
}

/**
 * Whether a departure is full, which is a different question from whether it is sold out.
 *
 * Nothing free and nothing held: every seat is somebody's. A departure with holds outstanding is
 * not full, because those holds expire.
 */
export function isFull(row: BoardRow): boolean {
  return row.occupancy.free === 0 && row.occupancy.held === 0;
}

/** `14:30`, the form every time on this console takes. */
export function clock(instant: string): string {
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return instant;
  return new Date(parsed).toISOString().slice(11, 16);
}

/** `23 Oct 14:30`, for a board that spans more than today. */
export function departsCell(instant: string): { readonly text: string; readonly title: string } {
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return { text: instant, title: instant };
  const when = new Date(parsed);
  const day = when.toISOString().slice(8, 10);
  const month = when.toUTCString().slice(8, 11);
  return {
    /*
     * The date as well as the time, because this listing is not bounded to today: MOB-04's `from`
     * is optional and the board does not set it. A column of bare times across three days is a
     * column that misleads at a glance, which is the one thing a dispatcher reads it for.
     */
    text: `${day} ${month} ${clock(instant)}`,
    title: at(instant) ?? instant,
  };
}

/** The four tiles, over every row rather than the filtered page. */
export interface DepartureTiles {
  readonly scheduled: number;
  readonly boarding: number;
  readonly full: number;
  readonly seatsHeld: number;
}

export function tilesFor(rows: readonly BoardRow[]): DepartureTiles {
  const live = rows.filter((row) => row.status === "scheduled" || row.status === "boarding");
  return {
    scheduled: rows.filter((row) => row.status === "scheduled").length,
    boarding: rows.filter((row) => row.status === "boarding").length,
    /*
     * Full counted over the live ones only. A departed bus is full or not and nobody can act on
     * it, and counting it would make the number grow all day and mean less by the hour.
     */
    full: live.filter(isFull).length,
    seatsHeld: live.reduce((total, row) => total + row.occupancy.held, 0),
  };
}

/* ------------------------------------------------------------------------- *
 * The data-grid toolbar, which design rule 4 requires of every grid.
 * ------------------------------------------------------------------------- */

export type DepartureOrder = "default" | "fullest" | "emptiest" | "route";

export interface DepartureQuery {
  readonly search: string;
  readonly status?: DepartureRow["status"];
  readonly order: DepartureOrder;
  readonly page: number;
  readonly perPage: number;
}

export interface DeparturePage {
  readonly rows: readonly BoardRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly BoardRow[];
  readonly total: number;
  readonly pageCount: number;
  readonly filtered: boolean;
}

function searchableText(row: BoardRow): string {
  return [row.routeName, row.departureId, row.vehicleId, row.status, badgeFor(row.status).label]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the board holds.
 *
 * `default` is MOB-04's own order — soonest first — and it is the one a board of departures must
 * not silently replace. The next bus leaving is the one somebody is about to be asked about.
 */
export function departurePage(rows: readonly BoardRow[], query: DepartureQuery): DeparturePage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      (query.status === undefined || row.status === query.status) &&
      (needle.length === 0 || searchableText(row).includes(needle)),
  );

  const share = (row: BoardRow): number =>
    row.occupancy.total === 0 ? 0 : row.occupancy.sold / row.occupancy.total;

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "route") return left.routeName.localeCompare(right.routeName);
          const compared = share(right) - share(left);
          return query.order === "fullest" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return {
    rows: ordered.slice(from, from + query.perPage),
    all: ordered,
    total,
    pageCount,
    filtered: needle.length > 0 || query.status !== undefined,
  };
}

/** The filter's choices, in the badge words so the two cannot disagree. */
export const STATUS_FILTERS: readonly {
  readonly value: DepartureRow["status"];
  readonly label: string;
}[] = [
  { value: "scheduled", label: "scheduled" },
  { value: "boarding", label: "boarding" },
  { value: "departed", label: "departed" },
  { value: "cancelled", label: "cancelled" },
];

export const ORDER_CHOICES: readonly {
  readonly value: DepartureOrder;
  readonly label: string;
}[] = [
  { value: "default", label: "Soonest first" },
  { value: "fullest", label: "Fullest first" },
  { value: "emptiest", label: "Emptiest first" },
  { value: "route", label: "Route A–Z" },
];

export const PER_PAGE_CHOICES: readonly number[] = [10, 25, 50];

export const EXPORT_HEADER: readonly string[] = ["Route", "Departs", "Seats", "State"];

export function exportRow(row: BoardRow): readonly string[] {
  return [
    row.routeName,
    at(row.departsAt) ?? row.departsAt,
    occupancyCell(row).text,
    badgeFor(row.status).label,
  ];
}

/** One departure's detail panel. */
export function departureDetail(row: BoardRow): RecordDetail {
  const badge = badgeFor(row.status);

  return {
    headline: row.routeName,
    identifier: row.departureId,
    source: "MOB-04",
    state: {
      classKey: badge.classKey,
      label: badge.label,
      code: "MOB-04",
      status: row.status,
    },
    fields: [
      field("Departs", at(row.departsAt) ?? row.departsAt),
      field("Seats sold", `${String(row.occupancy.sold)} of ${String(row.occupancy.total)}`),
      field("Held while somebody pays", String(row.occupancy.held)),
      field("Still free", String(row.occupancy.free)),
      field("Vehicle", row.vehicleId, true),
      /*
       * The route's identifier as well as its name, because the name is a join this board made
       * and the identifier is what MOB-04 actually answered. An operator reporting a problem
       * needs the one the service knows.
       */
      row.routeMissing
        ? absent(
            "Route",
            "MOB-04 answered a route identifier that its own route listing does not name. A retired route still names departures already scheduled on it, so this is a row to look at rather than a fault in the board.",
          )
        : field("Route", row.routeId, true),
      /*
       * The product is published and not resolved. MOB-01 owns what it is called, and this board
       * does not read MOB-01 — adding a third listing to name a column the design has no room for
       * would be a request per refresh for a string nobody is looking at.
       */
      field("Product", row.productId, true),
      ...optional("Cancelled because", row.cancelledReason),
      field("Scheduled", at(row.createdAt) ?? row.createdAt),
      field("Version", `v${String(row.version)}`),
    ],
    note:
      row.occupancy.held > 0
        ? "Held seats are not sales. A hold lapses on its own after fifteen minutes and the seat goes back into the count."
        : "Every seat sold on a departure becomes its own trip, joined to the others by this departure.",
  };
}
